import { parseISO } from 'date-fns';

/** Cascade priority for dynamic usage dimensions. Price stays after factor provider. */
const usageDimensionOrder = {
  finallocation: 4,
  originlocation: 5,
  factorprovider: 6,
  price: 7,
};

/**
 * True when a conversion factor does not use this dimension.
 * OpenSearch stores missing keywords as "NA".
 * @param {*} value
 * @returns {boolean}
 */
const isBlankDimension = (value) => value == null || value === '' || value === 'NA';

/**
 * Puts location, factor provider, and price into cascade order while leaving
 * unprioritised keys (vehicle, fuel, …) in their original relative positions.
 * @param {string[]} keys
 * @param {Record<string, number>} [order]
 * @returns {string[]}
 * @example
 * sortUsageDimensionKeys(['price', 'vehicle', 'originlocation', 'factorprovider'])
 * // ['originlocation', 'vehicle', 'factorprovider', 'price']
 */
const sortUsageDimensionKeys = (keys, order = usageDimensionOrder) => {
  const list = Array.isArray(keys) ? [...keys] : [];
  const slots = [];
  list.forEach((key, index) => {
    if (order[key] != null) slots.push(index);
  });
  const prioritized = list.filter((key) => order[key] != null).sort((a, b) => order[a] - order[b]);
  const result = list.slice();
  slots.forEach((slot, index) => {
    result[slot] = prioritized[index];
  });
  return result;
};

/**
 * Copies usage option lists so insertion order matches the cascade.
 * @param {Record<string, object[]>|null|undefined} usageOptions
 * @returns {Record<string, object[]>|null|undefined}
 */
const orderUsageOptions = (usageOptions) => {
  if (!usageOptions || typeof usageOptions !== 'object') return usageOptions;
  const ordered = {};
  sortUsageDimensionKeys(Object.keys(usageOptions)).forEach((key) => {
    ordered[key] = usageOptions[key];
  });
  return ordered;
};

const getUsageOptionsByMetricTypes = (metricTypes) => {
  const usageOptions = {};

  for (const [key, value] of Object.entries(metricTypes)) {
    usageOptions[key] = value;
  }

  return usageOptions;
};

const getUsageOptionsByFilteredCF = (usageOptions, filteredCF, item = false, constrainKeys = null) => {
  const usageOptionsKeys = Object.keys(usageOptions || {});

  const filteredUsageOptions = {};
  const keysToApply =
    constrainKeys ||
    sortUsageDimensionKeys(usageOptionsKeys.filter((key) => usageDimensionOrder[key] != null));

  // Only earlier cascade selections narrow this list. Price must not hide factor providers.
  filteredCF = filteredCF?.filter((cf) =>
    keysToApply.every((key) => isBlankDimension(item?.[key]) || String(cf?.[key]) === String(item[key]))
  );
  filteredCF?.forEach((cf) => {
    //looping through the keys of the options object with production,vehicle,fuel,etc
    usageOptionsKeys.forEach((usageOpt) => {
      const usageOptionId = isBlankDimension(cf[usageOpt]) ? null : cf[usageOpt];
      // Check if the usageOption exists in the filtered Conversion Factors
      const foundOptionObj =
        usageOptionId &&
        usageOptions?.[usageOpt]?.find((row) => String(row?.id) === String(usageOptionId));

      if (usageOptionId && foundOptionObj) {
        // Usage Option ID exists in the filtered CF -> It's a valid option
        if (!filteredUsageOptions[usageOpt]) {
          filteredUsageOptions[usageOpt] = [];
        } else if (filteredUsageOptions[usageOpt].find((row) => String(row?.id) === String(usageOptionId))) {
          // If the option already exists in the filteredUsageOptions, don't add it again
          return;
        }
        filteredUsageOptions[usageOpt].push(foundOptionObj);
      }
    });
  });

  return filteredUsageOptions;
};

const getFactorYear = (usageData, org) => {
  let reportingYearEndDate = {
    day: org?.reportingYearEndDay,
    month: org?.reportingYearEndMonth,
  };
  let factorYear = 0;
  let rYear = true;
  let dEnd = parseISO(usageData?.dateEnd);
  if (org?.reportingYearEndMonth == 12 && org?.reportingYearEndDay == 31) {
    rYear = false;
    factorYear = parseInt(usageData?.dateEnd?.substring(0, 4));
  } else if (dEnd?.getMonth() + 1 < org?.reportingYearEndMonth) {
    reportingYearEndDate.year = parseInt(usageData?.dateEnd?.substring(0, 4));
  } else if (dEnd?.getMonth() + 1 == org?.reportingYearEndMonth) {
    if (dEnd.getDate() <= org.reportingYearEndDay) {
      reportingYearEndDate.year = parseInt(usageData?.dateEnd?.substring(0, 4));
    } else {
      reportingYearEndDate.year = parseInt(usageData?.dateEnd?.substring(0, 4)) + 1;
    }
  } else reportingYearEndDate.year = parseInt(usageData?.dateEnd?.substring(0, 4)) + 1;

  if (rYear)
    if (org?.reportingYearEndMonth > 6 || (org?.reportingYearEndDay > 29 && org?.reportingYearEndMonth == 6)) {
      factorYear = reportingYearEndDate?.year;
    } else {
      factorYear = reportingYearEndDate?.year - 1;
    }

  return factorYear;
};

export {
  getUsageOptionsByFilteredCF,
  getUsageOptionsByMetricTypes,
  getFactorYear,
  isBlankDimension,
  sortUsageDimensionKeys,
  orderUsageOptions,
  usageDimensionOrder,
};
